/**
 * practiceGuides.ts — связь «страница практики → её углублённые материалы».
 *
 * Зачем. Сорок семь страниц сборки не имели НИ ОДНОЙ входящей ссылки: сорок
 * один разбор `/nyc-*` и четыре подстраницы семейного права. Существовали,
 * попадали в карту сайта — и были недостижимы изнутри сайта. Проверка битых
 * ссылок этого не видит: у ненаписанной ссылки нет цели, чтобы сломаться.
 *
 * Карта не перечисляется руками, а ВЫВОДИТСЯ из тех же данных, которыми живут
 * сами страницы: у каждого разбора есть `category` и `relatedPracticeAreas`,
 * у каждой подстраницы — `parentHref`. Перечисление разошлось бы с данными на
 * первом же добавленном материале и промолчало бы об этом.
 *
 * 🔴 Основная привязка идёт по `category` — по одной, ровно одной практике на
 * материал. Это гарантирует покрытие: любой разбор получает ссылку хотя бы с
 * одной страницы. Дополнительные связи из `relatedPracticeAreas` идут сверху и
 * ограничены по количеству — ограничивать МОЖНО только их, потому что
 * потеря такой ссылки не оставляет материал без входящих.
 */
import { nycPageList } from '@/data/nycPages';
import { practiceSubPages } from '@/data/practiceSubPages';

export interface PracticeGuide {
  title: string;
  url: string;
  /** true — практика назначена материалу как основная (гарантия покрытия). */
  primary: boolean;
}

/** Сколько дополнительных, неосновных материалов показывать сверх основных. */
const CROSS_LINK_CAP = 6;

function build(): Record<string, PracticeGuide[]> {
  const out: Record<string, PracticeGuide[]> = {};
  const push = (slug: string, g: PracticeGuide) => {
    (out[slug] ??= []).push(g);
  };

  // 1. Основная привязка: одна практика на материал, по его собственной категории.
  for (const p of nycPageList) {
    push(p.category, { title: p.h1, url: p.url, primary: true });
  }

  // 2. Подстраницы практик — к своему родителю. Тоже основная привязка.
  for (const sp of practiceSubPages) {
    const slug = sp.parentHref.replace(/\//g, '');
    push(slug, { title: sp.title, url: sp.url, primary: true });
  }

  // 3. Перекрёстные связи сверх основных, с ограничением на практику.
  for (const p of nycPageList) {
    for (const area of p.relatedPracticeAreas) {
      if (area === p.category) continue;
      const list = (out[area] ??= []);
      if (list.some((g) => g.url === p.url)) continue;
      if (list.filter((g) => !g.primary).length >= CROSS_LINK_CAP) continue;
      list.push({ title: p.h1, url: p.url, primary: false });
    }
  }

  // Основные — первыми, дальше алфавит: порядок не должен зависеть от того,
  // в каком порядке лежат записи в файле данных.
  for (const slug of Object.keys(out)) {
    out[slug].sort((a, b) =>
      a.primary === b.primary ? a.title.localeCompare(b.title) : a.primary ? -1 : 1,
    );
  }
  return out;
}

export const guidesByPractice: Record<string, PracticeGuide[]> = build();

/**
 * Материалы, не получившие НИ ОДНОЙ ссылки. Должно быть пусто; величина
 * печатается сборочной проверкой рядом со знаменателем, чтобы ноль нельзя
 * было спутать с отказом прибора.
 */
export function unlinkedGuides(): string[] {
  const linked = new Set(Object.values(guidesByPractice).flatMap((v) => v.map((g) => g.url)));
  return [
    ...nycPageList.map((p) => p.url),
    ...practiceSubPages.map((p) => p.url),
  ].filter((u) => !linked.has(u));
}

/**
 * Соседние материалы той же практики — чтобы разбор не висел на единственной
 * ссылке со своей страницы практики. Ссылки взаимные: если A показывает B,
 * то и B рано или поздно показывает A, потому что список берётся из одного
 * и того же порядка.
 */
export function siblingGuides(url: string, limit = 6): PracticeGuide[] {
  const page = nycPageList.find((p) => p.url === url);
  if (!page) return [];
  const pool = guidesByPractice[page.category] ?? [];
  const i = pool.findIndex((g) => g.url === url);
  if (i === -1) return pool.slice(0, limit);
  // Идём по кругу от собственной позиции: соседи у каждого разные, и ни один
  // материал не остаётся вне чужих списков.
  const out: PracticeGuide[] = [];
  for (let k = 1; k < pool.length && out.length < limit; k += 1) {
    out.push(pool[(i + k) % pool.length]);
  }
  return out;
}
